// src/pages/AboutPage.js

import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ThemeContext } from '../context/ThemeContext';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import { userService } from '../api';
import heros from '../assets/images/about.jpeg';

// Section principale à propos
const AboutSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const AboutContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4rem;
  margin-top: 4rem;
  align-items: center;
  width: 100%;

  @media (max-width: 992px) {
    flex-direction: column;
    gap: 3rem;
  }
`;


// Section de texte
const AboutText = styled.div`
  flex: 1;
  min-width: 300px;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: -15px;
    left: -15px;
    width: 50px;
    height: 50px;
    border-top: 4px solid ${props => props.theme.primary};
    border-left: 4px solid ${props => props.theme.primary};
    opacity: 0.7;
    z-index: 1;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    right: -15px;
    width: 50px;
    height: 50px;
    border-bottom: 4px solid ${props => props.theme.primary};
    border-right: 4px solid ${props => props.theme.primary};
    opacity: 0.7;
    z-index: 1;
  }
`;

const AboutImage = styled.div`
  flex: 1;
  min-width: 300px;
  max-width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadow || '0 10px 30px rgba(0, 0, 0, 0.15)'};

  img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    img {
      height: auto;
      object-fit: contain;
    }
  }
`;


// Paragraphes
const Paragraph = styled(motion.p)`
  margin-bottom: 1.8rem;
  color: ${props => props.theme.text};
  line-height: 1.8;
  font-size: 1.05rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

// Texte en gras
const StrongText = styled.strong`
  font-weight: 700;
  color: ${props => props.theme.primary};
`;

// Section de liste
const StyledList = styled.ul`
  padding-left: 1.5rem;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.text};
  
  li {
    margin-bottom: 0.8rem;
    position: relative;
    padding-left: 0.5rem;
    line-height: 1.6;
    
    &:before {
      content: "•";
      color: ${props => props.theme.primary};
      font-weight: bold;
      display: inline-block;
      width: 1em;
      margin-left: -1em;
    }
  }
`;

// Section Mission
const MissionSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.backgroundSecondary || '#f5f7fa'};
`;

// Cartes de mission
const MissionCard = styled(motion.div)`
  background: ${props => props.theme.card?.background || (props.theme.dark ? '#2d3748' : 'white')};
  border-radius: 12px;
  box-shadow: ${props => props.theme.card?.shadow || '0 10px 25px rgba(0, 0, 0, 0.1)'};
  padding: 2.5rem;
  margin-top: 2.5rem;
  position: relative;
  z-index: 1;
  border-left: 5px solid ${props => props.theme.primary};
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

// Titre de la mission
const MissionTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.primary};
  font-weight: 700;
  position: relative;
  padding-bottom: 0.8rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 80px;
    height: 4px;
    background: ${props => props.theme.primary};
    border-radius: 2px;
  }
`;

// Section Pourquoi nous
const WhyUsSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.background};
`;

// Contenu Pourquoi nous
const WhyUsContent = styled.div`
  background: ${props => props.theme.card?.background || (props.theme.dark ? '#2d3748' : 'white')};
  border-radius: 12px;
  padding: 3rem;
  margin-top: 3rem;
  box-shadow: ${props => props.theme.card?.shadow || '0 10px 25px rgba(0, 0, 0, 0.1)'};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '"';
    position: absolute;
    top: -40px;
    left: 20px;
    font-size: 200px;
    font-family: Georgia, serif;
    color: ${props => props.theme.primary}20;
    z-index: 0;
  }
`;

// Liste Pourquoi nous
const WhyUsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
  position: relative;
  z-index: 1;
`;

const WhyUsItem = styled(motion.li)`
  position: relative;
  padding-left: 2rem;
  margin-bottom: 1.2rem;
  font-size: 1.1rem;
  color: ${props => props.theme.text};
  line-height: 1.7;
  
  &:before {
    content: "✓";
    position: absolute;
    left: 0;
    color: ${props => props.theme.primary};
    font-weight: bold;
  }
`;

// Section Équipe
const TeamSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.backgroundSecondary || '#f5f7fa'};
`;

// Grille d'équipe
const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 3rem;
  margin-top: 4rem;
`;

// Carte de membre d'équipe
const TeamMember = styled(motion.div)`
  background: ${props => props.theme.card?.background || (props.theme.dark ? '#2d3748' : 'white')};
  border-radius: 12px;
  box-shadow: ${props => props.theme.card?.shadow || '0 10px 25px rgba(0, 0, 0, 0.1)'};
  padding: 2rem;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.card?.hoverShadow || '0 15px 35px rgba(0, 0, 0, 0.15)'};
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: ${props => props.theme.primary};
  }
`;

// Image de membre
const MemberImage = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background-image: url(${props => props.src || '/default-profile.jpg'});
  background-size: cover;
  background-position: center;
  margin: 0 auto 1.5rem;
  border: 4px solid ${props => props.theme.primary};
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

// Nom du membre
const MemberName = styled.h4`
  font-size: 1.4rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.text};
  font-weight: 700;
`;

// Rôle du membre
const MemberRole = styled.div`
  display: inline-block;
  font-size: 0.95rem;
  font-weight: 600;
  color: white;
  background: ${props => props.theme.primary};
  padding: 0.4rem 1rem;
  border-radius: 20px;
  margin-bottom: 1rem;
`;

// Biographie du membre
const MemberBio = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.text};
  opacity: 0.85;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

// Icônes sociales
const SocialIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.theme.backgroundSecondary || '#f5f7fa'};
  color: ${props => props.theme.text};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.primary};
    color: white;
    transform: translateY(-3px);
  }
  
  i {
    font-size: 1rem;
  }
`;

// Section Valeurs
const ValuesSection = styled.section`
  padding: 6rem 2rem;
  background: ${props => props.theme.background};
`;

// Grille de valeurs
const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2.5rem;
  margin-top: 3.5rem;
  position: relative;
  z-index: 1;
`;

// Carte de valeur
const ValueCard = styled(motion.div)`
  background: ${props => props.theme.card?.background || (props.theme.dark ? '#2d3748' : 'white')};
  border-radius: 12px;
  box-shadow: ${props => props.theme.card?.shadow || '0 10px 25px rgba(0, 0, 0, 0.1)'};
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease;
  border-top: 5px solid ${props => props.theme.primary};
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.theme.card?.hoverShadow || '0 15px 35px rgba(0, 0, 0, 0.15)'};
  }
`;

// Icône de valeur
const ValueIcon = styled.div`
  font-size: 2.2rem;
  color: ${props => props.theme.primary};
  margin-bottom: 1.5rem;
  width: 80px;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${props => props.theme.backgroundSecondary || '#f5f7fa'};
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    background: ${props => props.theme.primary}20;
  }
`;

// Titre de valeur
const ValueTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.text};
  font-weight: 700;
  position: relative;
  
  &:after {
    content: '';
    display: block;
    width: 40px;
    height: 3px;
    background: ${props => props.theme.primary};
    margin: 0.7rem auto 0;
    border-radius: 2px;
  }
`;

// Section CTA
const CtaSection = styled.section`
  padding: 8rem 2rem;
  text-align: center;
  background: ${props => props.theme.backgroundSecondary || '#f5f7fa'};
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: ${props => props.theme.primary};
  }
`;

// Composant principal
const AboutPage = () => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Animation sections
  const [aboutRef, aboutInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  const [missionRef, missionInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  const [whyUsRef, whyUsInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  const [valuesRef, valuesInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  // Charger les membres de l'équipe depuis l'API
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        const response = await userService.getAll();
        const users = Array.isArray(response.data) ? response.data : [];
        
        // Filtrer les utilisateurs pour obtenir ceux avec des profils complets
        const validTeamMembers = users.filter(user => 
          user.first_name && user.last_name && user.profile_picture
        );
        
        setTeamMembers(validTeamMembers);
      } catch (error) {
        console.error("Erreur lors du chargement des membres de l'équipe:", error);
        setTeamMembers([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeamMembers();
  }, []);
  
  // Exemple de valeurs de l'entreprise
  const values = [
    {
      id: 1,
      title: t('innovation'),
      description: t('innovationDesc') || "Nous repoussons constamment les limites pour créer des solutions novatrices qui répondent aux défis technologiques d'aujourd'hui et de demain.",
      icon: 'fa-lightbulb',
    },
    {
      id: 2,
      title: t('excellence'),
      description: t('excellenceDesc') || "Nous nous engageons à fournir un travail de la plus haute qualité, en respectant les normes les plus strictes dans tous nos projets.",
      icon: 'fa-star',
    },
    {
      id: 3,
      title: t('teamwork'),
      description: t('teamworkDesc') || "Nous croyons en la puissance de la collaboration et du travail d'équipe pour développer des solutions efficaces et créatives.",
      icon: 'fa-users',
    },
    {
      id: 4,
      title: t('clientFocus'),
      description: t('clientFocusDesc') || "Nous plaçons les besoins de nos clients au centre de tout ce que nous faisons, en offrant des solutions personnalisées qui dépassent leurs attentes.",
      icon: 'fa-heart',
    },
    {
      id: 5,
      title: t('integrity'),
      description: t('integrityDesc') || "Nous agissons avec honnêteté, transparence et éthique dans toutes nos interactions professionnelles.",
      icon: 'fa-shield-alt',
    },
    {
      id: 6,
      title: t('sustainability'),
      description: t('sustainabilityDesc') || "Nous nous engageons à développer des solutions qui sont non seulement efficaces, mais aussi durables et respectueuses de l'environnement.",
      icon: 'fa-leaf',
    }
  ];
  
  return (
    <>
      {/* Section À propos */}
      <AboutSection ref={aboutRef} theme={theme}>
        <Container>
          <SectionTitle
            title={t('aboutUs')}
            subtitle={t('aboutUsSubtitle') || "Découvrez notre histoire, notre mission et notre équipe"}
            highlight
            animated
          />
          
          <AboutContent>
            <AboutText theme={theme}>
              <Paragraph
                theme={theme}
                initial={{ opacity: 0, y: 20 }}
                animate={aboutInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5 }}
              >
                {t('aboutParagraph1') || "Notre mission chez Propenta TechSavving Solutions est de fournir des solutions technologiques innovantes qui transforment les défis en opportunités. Nous nous engageons à offrir une gamme complète de services, allant du développement web, mobile et desktop, à l'administration système, au déploiement d'applications, à la configuration et au déploiement réseau, ainsi qu'aux pratiques DevOps de premier ordre."}
              </Paragraph>
              
              <Paragraph
                theme={theme}
                initial={{ opacity: 0, y: 20 }}
                animate={aboutInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                {t('aboutParagraph2') || "Nous croyons en la création d'un impact significatif à travers des solutions personnalisées qui répondent aux besoins uniques de chaque client. Notre équipe dévouée s'efforce de surpasser les attentes en offrant des solutions efficaces, fiables et évolutives."}
              </Paragraph>
              
              <Paragraph
                theme={theme}
                initial={{ opacity: 0, y: 20 }}
                animate={aboutInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {t('aboutParagraph3') || "À Propenta, nous nous engageons à accompagner nos clients à chaque étape de leur parcours technologique, en les aidant à atteindre leurs objectifs commerciaux et à maximiser leur succès numérique."}
              </Paragraph>
            </AboutText>
            
            <AboutImage
              theme={theme}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={aboutInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.5 }}
            >
              <img src={heros} alt="Illustration à propos" />
            </AboutImage>

          </AboutContent>
        </Container>
      </AboutSection>
      
      {/* Section Mission */}
      <MissionSection theme={theme} ref={missionRef}>
        <Container>
          <SectionTitle
            title={t('ourMission')}
            subtitle={t('ourMissionSubtitle') || "Propulser votre entreprise vers l'avenir grâce à l'innovation"}
            highlight
            animated
          />
          
          <MissionCard 
            theme={theme}
            initial={{ opacity: 0, y: 30 }}
            animate={missionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.5 }}
          >
            <MissionTitle theme={theme}>{t('missionTitle') || "Notre mission"}</MissionTitle>
            <Paragraph theme={theme}>
              {t('missionDescription') || "Notre mission chez Propenta TechSavving Solutions est de fournir des solutions technologiques innovantes qui transforment les défis en opportunités. Nous nous engageons à offrir une gamme complète de services, allant du développement web, mobile et desktop, à l'administration système, au déploiement d'applications, à la configuration et au déploiement réseau, ainsi qu'aux pratiques DevOps de premier ordre. Nous croyons en la création d'un impact significatif à travers des solutions personnalisées qui répondent aux besoins uniques de chaque client. Notre équipe dévouée s'efforce de surpasser les attentes en offrant des solutions efficaces, fiables et évolutives. À Propenta, nous nous engageons à accompagner nos clients à chaque étape de leur parcours technologique, en les aidant à atteindre leurs objectifs commerciaux et à maximiser leur succès numérique."}
            </Paragraph>
          </MissionCard>
          
          <MissionCard 
            theme={theme}
            initial={{ opacity: 0, y: 30 }}
            animate={missionInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <MissionTitle theme={theme}>{t('visionTitle') || "Notre vision"}</MissionTitle>
            <Paragraph theme={theme}>
              {t('visionDescription') || "Chez Propenta TechSavving Solutions, nous aspirons à être des pionniers dans le domaine de la technologie en offrant des solutions novatrices qui repoussent les limites de l'innovation. Notre vision est de devenir le partenaire de confiance de nos clients, en les aidant à réaliser leur plein potentiel numérique."}
            </Paragraph>
            <StyledList theme={theme}>
              <li>Nous nous efforçons de créer des solutions qui simplifient les défis technologiques et améliorent l'efficacité opérationnelle.</li>
              <li>Nous cherchons à inspirer la confiance et la satisfaction de nos clients en offrant des services de qualité supérieure et en dépassant continuellement leurs attentes.</li>
              <li>Nous visons à être à la pointe de l'industrie en adoptant les dernières technologies et en restant à l'affût des tendances émergentes.</li>
            </StyledList>
          </MissionCard>
        </Container>
      </MissionSection>
      
      {/* Section Pourquoi nous */}
      <WhyUsSection ref={whyUsRef} theme={theme}>
        <Container>
          <SectionTitle
            title={t('whyUs') || "Pourquoi nous ?"}
            subtitle={t('whyUsSubtitle') || "Ce qui nous distingue de la concurrence"}
            highlight
            animated
          />
          
          <WhyUsContent theme={theme}>
            <Paragraph
              theme={theme}
              initial={{ opacity: 0, y: 20 }}
              animate={whyUsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
            >
              {t('whyUsIntro') || "Propenta TechSavving Solutions se distingue par son engagement envers l'excellence et son approche centrée sur le client. Voici quelques raisons pour lesquelles nous sommes le partenaire idéal pour vos besoins technologiques :"}
            </Paragraph>
            
            <WhyUsList theme={theme}>
              <WhyUsItem
                theme={theme}
                initial={{ opacity: 0, x: -20 }}
                animate={whyUsInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <StrongText theme={theme}>Expertise diversifiée :</StrongText> Notre équipe possède une vaste expérience dans une gamme variée de services technologiques, allant du développement web, mobile à l'administration système, au déploiement d'applications, à la configuration et au déploiement réseau, ainsi qu'aux pratiques DevOps.
              </WhyUsItem>
              
              <WhyUsItem
                theme={theme}
                initial={{ opacity: 0, x: -20 }}
                animate={whyUsInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <StrongText theme={theme}>Approche personnalisée :</StrongText> Nous comprenons que chaque entreprise a des besoins uniques. C'est pourquoi nous collaborons étroitement avec nos clients pour fournir des solutions sur mesure qui répondent spécifiquement à leurs objectifs et à leurs défis.
              </WhyUsItem>
              
              <WhyUsItem
                theme={theme}
                initial={{ opacity: 0, x: -20 }}
                animate={whyUsInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <StrongText theme={theme}>Qualité et fiabilité :</StrongText> Nous nous engageons à fournir des solutions de la plus haute qualité, en utilisant les meilleures pratiques de l'industrie et en garantissant la fiabilité de nos services.
              </WhyUsItem>
              
              <WhyUsItem
                theme={theme}
                initial={{ opacity: 0, x: -20 }}
                animate={whyUsInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <StrongText theme={theme}>Innovation continue :</StrongText> Nous restons à l'avant-garde de la technologie en suivant de près les dernières tendances et en adoptant les innovations les plus récentes pour offrir à nos clients des solutions innovantes et compétitives.
              </WhyUsItem>
            </WhyUsList>
          </WhyUsContent>
        </Container>
      </WhyUsSection>
      
      {/* Section Équipe */}
      <TeamSection theme={theme}>
        <Container>
          <SectionTitle
            title={t('ourTeam')}
            subtitle={t('ourTeamSubtitle') || "Rencontrez les experts derrière nos solutions"}
            highlight
            animated
          />
          
          <TeamGrid>
            {teamMembers.map((member, index) => (
              <TeamMember
                key={member.id}
                theme={theme}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <MemberImage src={member.profile_picture} theme={theme} />
                <MemberName theme={theme}>{`${member.first_name} ${member.last_name}`}</MemberName>
                <MemberRole theme={theme}>{member.position || member.skills || "Expert Tech"}</MemberRole>
                <MemberBio theme={theme}>{member.bio || "Passionné de technologie et engagé dans l'excellence."}</MemberBio>
                
                <SocialIcons theme={theme}>
                  {member.linkedin_profile && (
                    <SocialIcon href={member.linkedin_profile} target="_blank" rel="noopener noreferrer" theme={theme}>
                      <i className="fab fa-linkedin-in"></i>
                    </SocialIcon>
                  )}
                  {member.github_profile && (
                    <SocialIcon href={member.github_profile} target="_blank" rel="noopener noreferrer" theme={theme}>
                      <i className="fab fa-github"></i>
                    </SocialIcon>
                  )}
                  {member.twitter_profile && (
                    <SocialIcon href={member.twitter_profile} target="_blank" rel="noopener noreferrer" theme={theme}>
                      <i className="fab fa-twitter"></i>
                    </SocialIcon>
                  )}
                  {member.facebook_profile && (
                    <SocialIcon href={member.facebook_profile} target="_blank" rel="noopener noreferrer" theme={theme}>
                      <i className="fab fa-facebook-f"></i>
                    </SocialIcon>
                  )}
                </SocialIcons>
              </TeamMember>
            ))}
          </TeamGrid>
        </Container>
      </TeamSection>
      
      {/* Section Valeurs */}
      <ValuesSection theme={theme} ref={valuesRef}>
        <Container>
          <SectionTitle
            title={t('ourValues')}
            subtitle={t('ourValuesSubtitle') || "Les principes qui guident nos actions"}
            highlight
            animated
          />
          
          <ValuesGrid>
            {values.map((value, index) => (
              <ValueCard
                key={value.id}
                theme={theme}
                initial={{ opacity: 0, y: 30 }}
                animate={valuesInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <ValueIcon theme={theme}>
                  <i className={`fas ${value.icon}`} />
                </ValueIcon>
                <ValueTitle theme={theme}>{value.title}</ValueTitle>
                <Paragraph theme={theme}>
                  {value.description}
                </Paragraph>
              </ValueCard>
            ))}
          </ValuesGrid>
        </Container>
      </ValuesSection>
      
      {/* Section CTA */}
      <CtaSection theme={theme}>
        <Container>
          <SectionTitle
            title={t('joinOurTeam')}
            subtitle={t('joinOurTeamSubtitle') || "Faites partie de notre aventure et contribuez à façonner l'avenir technologique"}
            highlight
            animated
          />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Button
              variant="primary"
              size="large"
              to="/community"
            >
              {t('applyNow') || "Postuler maintenant"}
            </Button>
          </motion.div>
        </Container>
      </CtaSection>
    </>
  );
};

export default AboutPage;